// ─── Odoo RPC Configuration — Updated ─────────────────────────────────────────
export const DB = "mlm_platform_userdata";
const RPC_ENDPOINT =
  process.env.NODE_ENV === "production"
    ? "https://crypto.demo.xamta.in/api/jsonrpc"
    : "/jsonrpc";

// ─── Core JSON-RPC helper ───────────────────────────────────────────────────
export async function rpc(service, method, args) {
  let response;
  try {
    response = await fetch(RPC_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        method: "call",
        params: { service, method, args },
        id: Date.now(),
      }),
    });
  } catch (networkErr) {
    throw new Error("Unable to connect. Please check your internet connection.");
  }

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    await response.text();
    throw new Error("Server is currently unavailable. Please try again later.");
  }

  const data = await response.json();
  if (data.error) throw new Error(data.error.data?.message || data.error.message);
  return data.result;
}

// ─── Authenticate ────────────────────────────────────────────────────────────
export async function authenticate(username, password) {
  const uid = await rpc("common", "authenticate", [DB, username, password, {}]);
  if (!uid) throw new Error("Invalid Username & Password.");
  return uid;
}

// ─── Check if logged-in user is admin ───────────────────────────────────────
export async function isAdminUser(uid, password) {
  try {
    const records = await rpc("object", "execute_kw", [
      DB, uid, password,
      "res.users", "read",
      [[uid], ["login", "groups_id"]]
    ]);
    const user = records[0];
    if (!user) return false;
    if (user.login === "admin") return true;
    const adminGroup = await rpc("object", "execute_kw", [
      DB, uid, password,
      "res.groups", "search",
      [[["full_name", "ilike", "Administration / Settings"]]]
    ]);
    if (adminGroup.length > 0 && user.groups_id.includes(adminGroup[0])) return true;
    return false;
  } catch {
    return false;
  }
}

export async function fetchCurrentUserInfo(uid, password) {
  try {
    const records = await rpc("object", "execute_kw", [
      DB, uid, password,
      "res.users", "read",
      [[uid], ["name", "partner_id", "email", "login"]]
    ]);
    return records[0] || null;
  } catch (e) {
    return null;
  }
}

export async function fetchCurrentUserName(uid, password) {
  const info = await fetchCurrentUserInfo(uid, password);
  return info ? info.name : "User";
}

// ─── Profile Fields ────────────────────────────────────────────────────────
const PROFILE_FIELDS = [
  "street", "name", "street2", "city", "zip", "state_id", "country_id",
  "phone", "email", "mlm_sequence", "parent_id",
  "ltx",
  "payment_type", "bank_account_name", "bank_account_number",
  "bank_account_type", "bank_ifsc_code", "bank_branch", "bank_name",
  "upi_reference_code", "payment_gateway_reference",
  "total_direct_child", "total_amount", "child_total_amount", "is_mlm_member", "mlm_treepath", "status", "post_id",
];

export async function fetchCommissions(uid, password, partnerId, options = {}) {
  const { offset = 0, limit = 1000, filter = "all", month = "", minAmount = 0, sort = "trans_date desc, id desc", fromUserId = null } = options;
  const domain = [["partner_id", "=", partnerId]];
  if (filter === "paid") domain.push(["is_paid", "=", true]);
  else if (filter === "unpaid") domain.push(["is_paid", "=", false]);

  if (month) {
    domain.push(["trans_date", "ilike", `${month}%`]);
  }
  if (minAmount > 0) {
    domain.push(["commission_amount", ">=", minAmount]);
  }
  if (fromUserId) {
    domain.push(["child_partner_id", "=", fromUserId]);
  }

  const fields = ["id", "partner_id", "child_partner_id", "post_id", "node_id", "commission_amount", "trans_date", "transaction_id", "create_date", "is_paid", "commission_recid","level_name"];

  const result = await rpc("object", "execute_kw", [
    DB, uid, password,
    "mlm.transaction.commission", "search_read",
    [domain],
    {
      fields,
      offset,
      limit,
      order: sort
    }
  ]);
  return result;}

export async function fetchIncentiveCommissions(uid, password) {
  try {
    const fields = ["id", "serial_number", "level", "commission_percentage", "min_legs", "min_range", "max_range", "post_id"];
    const records = await rpc("object", "execute_kw", [
      DB, uid, password,
      "mlm.commission", "search_read",
      [[]],
      { fields, order: "serial_number asc" }
    ]);
    return records || [];
  } catch (e) {
    console.error("Failed to fetch incentive commissions:", e);
    return [];
  }
}

export async function fetchTravelCommissions(uid, password) {
  try {
    const fields = ["id", "serial_number", "post_id", "commission_percentage", "min_legs", "min_budget"];
    const records = await rpc("object", "execute_kw", [
      DB, uid, password,
      "mlm.travel.incentive", "search_read",
      [[]],
      { fields, order: "serial_number asc" }
    ]);
    return records || [];
  } catch (e) {
    console.error("Failed to fetch incentive commissions:", e);
    return [];
  }
}
export async function fetchCarCommissions(uid, password) {
  try {
    const fields = ["id", "serial_number", "name", "post_id", "commission_percentage", "min_legs", "min_budget"];
    const records = await rpc("object", "execute_kw", [
      DB, uid, password,
      "mlm.car.incentive", "search_read",
      [[]],
      { fields, order: "serial_number asc" }
    ]);
    return records || [];
  } catch (e) {
    console.error("Failed to fetch car commissions:", e);
    return [];
  }
}

export async function fetchHouseCommissions(uid, password) {
  try {
    const fields = ["id", "serial_number", "name", "post_id", "commission_percentage", "min_legs", "min_budget"];
    const records = await rpc("object", "execute_kw", [
      DB, uid, password,
      "mlm.house.incentive", "search_read",
      [[]],
      { fields, order: "serial_number asc" }
    ]);
    return records || [];
  } catch (e) {
    console.error("Failed to fetch car commissions:", e);
    return [];
  }
}

export async function fetchLifestyleCommissions(uid, password) {
  try {
    const fields = ["id", "serial_number", "post_id", "commission_percentage", "min_legs", "min_budget"];
    const records = await rpc("object", "execute_kw", [
      DB, uid, password,
      "mlm.lifestyle.incentive", "search_read",
      [[]],
      { fields, order: "serial_number asc" }
    ]);
    return records || [];
  } catch (e) {
    console.error("Failed to fetch lifestyle commissions:", e);
    return [];
  }
}



export async function countCommissions(uid, password, partnerId, options = {}) {
  const { filter = "all", month = "", minAmount = 0, fromUserId = null } = options;
  const domain = [["partner_id", "=", partnerId]];
  if (filter === "paid") domain.push(["is_paid", "=", true]);
  else if (filter === "unpaid") domain.push(["is_paid", "=", false]);

  if (month) {
    domain.push(["trans_date", "ilike", `${month}%`]);
  }
  if (minAmount > 0) {
    domain.push(["commission_amount", ">=", minAmount]);
  }
  if (fromUserId) {
    domain.push(["child_partner_id", "=", fromUserId]);
  }

  const result = await rpc("object", "execute_kw", [
    DB, uid, password,
    "mlm.transaction.commission", "search_count",
    [domain],
  ]);
  return result;
}

export async function fetchCommissionStats(uid, password, partnerId, options = {}) {
  const { month = "", minAmount = 0, fromUserId = null } = options;
  const baseDomain = [["partner_id", "=", partnerId]];
  // const baseDomain = [];
  if (month) baseDomain.push(["trans_date", "ilike", `${month}%`]);
  if (minAmount > 0) baseDomain.push(["commission_amount", ">=", minAmount]);
  if (fromUserId) baseDomain.push(["child_partner_id", "=", fromUserId]);

  // Use read_group to get sums grouped by is_paid status
  const groups = await rpc("object", "execute_kw", [
    DB, uid, password,
    "mlm.transaction.commission", "read_group",
    [baseDomain, ["commission_amount"], ["is_paid"]],
  ]);

  let total = 0;
  let paid = 0;
  let unpaid = 0;

  groups.forEach(g => {
    total += g.commission_amount || 0;
    if (g.is_paid) paid += g.commission_amount || 0;
    else unpaid += g.commission_amount || 0;
  });

  return { total, paid, unpaid };
}

export async function fetchCommissionFromUsers(uid, password, partnerId) {
  const domain = [["partner_id", "=", partnerId]];
  const groups = await rpc("object", "execute_kw", [
    DB, uid, password,
    "mlm.transaction.commission", "read_group",
    [domain, ["child_partner_id", "commission_amount"], ["child_partner_id"]],
  ]);

  return groups
    .filter(g => g.child_partner_id)
    .map(g => ({
      id: g.child_partner_id[0],
      name: g.child_partner_id[1],
      count: g.child_partner_id_count,
      totalAmount: g.commission_amount || 0
    }));
}

export async function fetchLevelData(uid, password, partnerId) {
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "read",
    [[partnerId], PROFILE_FIELDS]
  ]);
  return records[0] || null;
}

export async function fetchProfile(uid, password, partnerId) {
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "read",
    [[partnerId], PROFILE_FIELDS]
  ]);
  return records[0] || null;
}

export async function updateProfile(uid, password, partnerId, data) {
  const success = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "write",
    [[partnerId], data]
  ]);
  return success;
}

export async function setReferralByCode(uid, password, partnerId, code) {
  const result = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "set_referral_bycode",
    [[partnerId], code]
  ]);
  return result;
}

export async function updateUserEmail(uid, password, email) {
  const success = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.users", "write",
    [[uid], { email }]
  ]);
  return success;
}

// ==========================================
// TRANSACTIONS
// ==========================================
const TRANSACTION_MODEL = "mlm.transaction";

export async function fetchTransactions(uid, password, partnerId, { offset = 0, limit = 5, filter = "all" } = {}) {
  const domain = [["amount", ">", 0]];
  if (partnerId) {
    domain.push(["partner_id", "=", partnerId]);
  }
  if (filter === "approved") domain.push(["state", "=", "approved"]);
  else if (filter === "pending") domain.push(["state", "=", "pending"]);


  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    TRANSACTION_MODEL, "search_read",
    [domain],
    {
      fields: ["id", "transaction_number", "partner_id", "node_id", "ltx", "trans_date", "amount", "meta", "attachment", "state", "create_date", "post_id"],
      offset,
      limit,
      order: "trans_date desc, id desc"
    }
  ]);
  return records || [];
}

export async function countTransactions(uid, password, partnerId, { filter = "all" } = {}) {
  const domain = [];
  if (partnerId) {
    domain.push(["partner_id", "=", partnerId]);
  }
  if (filter === "approved") domain.push(["state", "=", "approved"]);
  else if (filter === "pending") domain.push(["state", "=", "pending"]);

  const count = await rpc("object", "execute_kw", [
    DB, uid, password,
    TRANSACTION_MODEL, "search_count",
    [domain],
  ]);
  return count;
}

export async function createTransaction(uid, password, data) {
  const newId = await rpc("object", "execute_kw", [
    DB, uid, password,
    TRANSACTION_MODEL, "create",
    [data]
  ]);
  return newId;
}

export async function fetchCountries(uid, password) {
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.country", "search_read",
    [[]],
    { fields: ["id", "name"], order: "name asc" }
  ]);
  return records;
}

export async function fetchStates(uid, password, countryId) {
  if (!countryId) return [];
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.country.state", "search_read",
    [[["country_id", "=", countryId]]],
    { fields: ["id", "name"], order: "name asc" }
  ]);
  return records;
}

export async function fetchAvailableParents(uid, password, currentPartnerId) {
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_read",
    [[["is_mlm_member", "=", true], ["id", "!=", currentPartnerId]]],
    { fields: ["id", "name", "mlm_sequence", 'post_id', 'total_amount', 'status'], limit: 1000, order: "name asc" }
  ]);
  return records;
}

// ─── Register User via Admin Proxy ──────────────────────────────────────────
export async function registerUser(name, username, password) {
  const ADMIN_USER = process.env.REACT_APP_ADMIN_USER || "admin";
  const ADMIN_PASS = process.env.REACT_APP_ADMIN_PASS || "Xamta@2050";

  let adminUid;
  try {
    adminUid = await authenticate(ADMIN_USER, ADMIN_PASS);
  } catch (e) {
    throw new Error("Admin authentication failed: " + (e.message || e));
  }

  if (!adminUid) {
    throw new Error("Admin authentication returned no UID. Check REACT_APP_ADMIN_USER/REACT_APP_ADMIN_PASS.");
  }

  try {
    const userId = await rpc("object", "execute_kw", [
      DB, adminUid, ADMIN_PASS,
      "res.users", "create",
      [{ name, login: username, password, is_mlm_member: true }]
    ]);
    return userId;
  } catch (e) {
    throw new Error("Failed to create user via RPC: " + (e.message || e));
  }
}

export const MLM_FIELDS = [
  "id", "name", "email", "is_mlm_member", "mlm_sequence",
  "parent_id", "post_id", "mlm_treepath",
  "total_direct_child", "total_amount", "child_total_amount",
  "status", "total_commission"
];

export async function fetchMembersByLevel(uid, password, rootPartnerId, level, { offset = 0, limit = 8, search = "", sort = "id desc", status = "" } = {}) {
  // BFS to get IDs at exact level
  let currentIds = [rootPartnerId];

  for (let l = 0; l < level; l++) {
    if (!currentIds.length) return { records: [], total: 0 };
    const children = await rpc("object", "execute_kw", [
      DB, uid, password,
      "res.partner", "search_read",
      [[["parent_id", "in", currentIds], ["is_mlm_member", "=", true]]],
      { fields: ["id"], limit: 5000 }
    ]);
    currentIds = children.map(c => c.id);
  }

  if (!currentIds.length) return { records: [], total: 0 };

  // Now currentIds = all IDs at target level — apply search + paginate
  const domain = [["id", "in", currentIds]];
  if (search) {
    domain.push("|");
    domain.push(["name", "ilike", search]);
    domain.push(["mlm_sequence", "ilike", search]);
  }
  if (status) {
    domain.push(["status", "=", status]);
  }

  const [total, records] = await Promise.all([
    rpc("object", "execute_kw", [
      DB, uid, password,
      "res.partner", "search_count",
      [domain]
    ]),
    rpc("object", "execute_kw", [
      DB, uid, password,
      "res.partner", "search_read",
      [domain],
      { fields: ["id", "name", "mlm_sequence", "post_id", "total_amount", "status", "total_commission"], offset, limit, order: sort }
    ])
  ]);

  // Fetch additional fields from history model if possible
  if (records.length > 0) {
    try {
      const partnerIds = records.map(r => r.id);
      const history = await rpc("object", "execute_kw", [
        DB, uid, password,
        "mlm.position.history", "search_read",
        [[["partner_id", "in", partnerIds]]],
        { fields: ["partner_id", "total_transaction", "total_commission"], order: "id desc" }
      ]);

      const latestHistoryByPartner = {};
      history.forEach(h => {
        const pid = h.partner_id[0];
        if (!latestHistoryByPartner[pid]) {
          latestHistoryByPartner[pid] = h;
        }
      });

      records.forEach(r => {
        const h = latestHistoryByPartner[r.id];
        if (h) {
          r.total_transaction = h.total_transaction || 0;
          if (h.total_commission !== undefined) r.total_commission = h.total_commission;
        } else {
          r.total_transaction = 0;
        }
      });
    } catch (e) {
      console.error("Failed to fetch extended fields from history:", e);
      // fallback
      records.forEach(r => { if (r.total_transaction === undefined) r.total_transaction = 0; });
    }
  }

  return { records, total };
}

export async function fetchRankCountsByLevel(uid, password, rootPartnerId, level, { search = "", status = "" } = {}) {
  // BFS to get IDs at exact level (same logic as fetchMembersByLevel)
  let currentIds = [rootPartnerId];
  for (let l = 0; l < level; l++) {
    if (!currentIds.length) return [];
    const children = await rpc("object", "execute_kw", [
      DB, uid, password,
      "res.partner", "search_read",
      [[["parent_id", "in", currentIds], ["is_mlm_member", "=", true]]],
      { fields: ["id"], limit: 5000 }
    ]);
    currentIds = children.map(c => c.id);
  }

  if (!currentIds.length) return [];

  const domain = [["id", "in", currentIds]];
  if (search) {
    domain.push("|");
    domain.push(["name", "ilike", search]);
    domain.push(["mlm_sequence", "ilike", search]);
  }
  if (status) domain.push(["status", "=", status]);

  const groups = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "read_group",
    [domain, ["post_id"], ["post_id"]],
  ]);

  return groups.map(g => ({
    rank: g.post_id?.[1] || "Member",
    count: g.post_id_count || 0
  }));
}
export async function countMlmMembers(uid, password) {
  const count = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_count",
    [[["is_mlm_member", "=", true], ["type", "=", "contact"]]],
  ]);
  return count;
}

export async function countUserNetwork(uid, password, treepath, partnerId = null) {
  if (!treepath) return 0;
  const domain = [
    ["mlm_treepath", "like", `${treepath}%`],
    ["is_mlm_member", "=", true],
    ["type", "=", "contact"]
  ];
  if (partnerId) domain.push(["id", "!=", partnerId]);
  const count = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_count",
    [domain],
  ]);
  return count;
}

export async function countNetworkByStatus(uid, password, treepath, status) {
  if (!treepath) return 0;
  const count = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_count",
    [[
      ["mlm_treepath", "like", `${treepath}%`],
      ["is_mlm_member", "=", true],
      ["status", "=", status],
    ]],
  ]);
  return count;
}

export async function fetchDirectStatusCounts(uid, password, partnerId) {
  if (!partnerId) return { active: 0, deactive: 0 };
  try {
    const [activeCount, deactiveCount] = await Promise.all([
      rpc("object", "execute_kw", [
        DB, uid, password,
        "res.partner", "search_count",
        [[["parent_id", "=", partnerId], ["is_mlm_member", "=", true], ["status", "=", "active"]]],
      ]),
      rpc("object", "execute_kw", [
        DB, uid, password,
        "res.partner", "search_count",
        [[["parent_id", "=", partnerId], ["is_mlm_member", "=", true], ["status", "=", "deactive"]]],
      ]),
    ]);
    return { active: activeCount || 0, deactive: deactiveCount || 0 };
  } catch (e) {
    return { active: 0, deactive: 0 };
  }
}

export async function fetchUserNetworkPage(uid, password, treepath, { offset = 0, limit = 50 } = {}) {
  if (!treepath) return [];
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_read",
    [[["mlm_treepath", "like", `${treepath}%`], ["is_mlm_member", "=", true]]],
    { fields: MLM_FIELDS, limit, offset, order: "mlm_sequence asc, id asc" },
  ]);
  return records;
}

export async function fetchMlmPage(uid, password, { offset = 0, limit = 50 } = {}) {
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_read",
    [[["is_mlm_member", "=", true]]],
    { fields: MLM_FIELDS, limit, offset, order: "id asc" },
  ]);
  return records;
}



export async function fetchChildren(uid, password, parentId) {
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_read",
    [[["parent_id", "=", parentId], ["is_mlm_member", "=", true]]],
    { fields: MLM_FIELDS, limit: 200, order: "mlm_sequence asc, id asc" },
  ]);
  return records;
}

export async function fetchMemberById(uid, password, id) {
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_read",
    [[["id", "=", id]]],
    { fields: MLM_FIELDS, limit: 1 },
  ]);
  return records[0] || null;
}

export async function fetchAllDescendants(uid, password, member) {
  const treepath = member.mlm_treepath;
  let domain;
  if (treepath) {
    domain = [
      ["mlm_treepath", "like", `${treepath}%`],
      ["is_mlm_member", "=", true],
      ["id", "!=", member.id],
    ];
  } else {
    return fetchDescendantsBFS(uid, password, member.id);
  }
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_read",
    [domain],
    { fields: MLM_FIELDS, limit: 2000, order: "mlm_sequence asc, id asc" },
  ]);
  return records;
}

async function fetchDescendantsBFS(uid, password, rootId) {
  const all = [];
  let queue = [rootId];
  while (queue.length > 0) {
    const children = await rpc("object", "execute_kw", [
      DB, uid, password,
      "res.partner", "search_read",
      [[["parent_id", "in", queue], ["is_mlm_member", "=", true]]],
      { fields: MLM_FIELDS, limit: 1000, order: "mlm_sequence asc" },
    ]);
    all.push(...children);
    queue = children.map(c => c.id);
  }
  return all;
}

export async function fetchMlmRoots(uid, password) {
  return await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_read",
    [[["parent_id", "=", false], ["is_mlm_member", "=", true]]],
    { fields: MLM_FIELDS, limit: 10, order: "id asc" }
  ]);
}

export async function fetchAncestors(uid, password, members, alreadyFetchedIds = new Set()) {
  if (!Array.isArray(members)) members = [members];
  members.forEach(m => alreadyFetchedIds.add(m.id));
  const toFetchIds = new Set();
  members.forEach(m => {
    if (m.mlm_treepath) {
      const pathIds = m.mlm_treepath.split("/").filter(Boolean).map(Number);
      pathIds.forEach(id => { if (!alreadyFetchedIds.has(id)) toFetchIds.add(id); });
    } else if (m.parent_id && m.parent_id[0]) {
      if (!alreadyFetchedIds.has(m.parent_id[0])) toFetchIds.add(m.parent_id[0]);
    }
  });
  if (toFetchIds.size === 0) return [];
  const records = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_read",
    [[["id", "in", Array.from(toFetchIds)]]],
    { fields: MLM_FIELDS },
  ]);
  const more = await fetchAncestors(uid, password, records, alreadyFetchedIds);
  return [...records, ...more];
}

export function buildHierarchy(records) {
  const sorted = [...records].sort((a, b) => {
    const lenA = (a.mlm_treepath || "").split("/").length;
    const lenB = (b.mlm_treepath || "").split("/").length;
    if (lenA !== lenB) return lenA - lenB;
    return (a.mlm_sequence || 0) - (b.mlm_sequence || 0) || a.id - b.id;
  });
  const map = {};
  const roots = [];
  sorted.forEach(r => { map[r.id] = { ...r, children: [] }; });
  sorted.forEach(r => {
    const pid = r.parent_id ? r.parent_id[0] : null;
    if (pid && map[pid]) map[pid].children.push(map[r.id]);
    else roots.push(map[r.id]);
  });
  return roots;
}

export function computeDepths(nodes, depth = 0) {
  return nodes.map(n => ({
    ...n, depth,
    children: computeDepths(n.children, depth + 1),
  }));
}

export async function fetchMlmProfile(uid, password, partnerId) {
  const records = await rpc("object", "execute_kw", [
    DB, uid, password, "res.partner", "read",
    [[partnerId], MLM_FIELDS],
  ]);
  return records[0] || null;
}

export async function fetchFullProfile(uid, password, partnerId) {
  const ALL_FIELDS = [
    ...new Set([
      "street", "name", "street2", "city", "zip", "state_id", "country_id",
      "phone", "email", "is_mlm_member", "mlm_sequence", "parent_id",
      "post_id", "mlm_treepath", "total_direct_child", "total_amount",
      "child_total_amount", "status", "ltx",
      "payment_type", "bank_account_name", "bank_account_number",
      "bank_account_type", "bank_ifsc_code", "bank_branch", "bank_name",
      "upi_reference_code", "payment_gateway_reference",
      "total_commission", "total_paid_commission", "total_unpaid_commission",
    ])
  ];
  const records = await rpc("object", "execute_kw", [
    DB, uid, password, "res.partner", "read",
    [[partnerId], ALL_FIELDS],
  ]);
  return records[0] || null;
}

// ─── Fetch only descendants of a specific partner (non-admin tree) ───────────
//
// FIXED: The original used `%/${rootPartnerId}/%` which excluded direct
// children (their treepath ends with `/${rootPartnerId}` not `/${rootPartnerId}/`).
// We now use two OR-conditions to catch both patterns, plus a fallback to
// `parent_id = rootPartnerId` for members whose treepath may not be set.
//
export async function fetchDescendantsByRoot(uid, password, rootPartnerId) {
  const all = [];
  let queue = [rootPartnerId];
  while (queue.length > 0) {
    const children = await rpc("object", "execute_kw", [
      DB, uid, password,
      "res.partner", "search_read",
      [[["parent_id", "in", queue], ["is_mlm_member", "=", true]]],
      { fields: MLM_FIELDS, limit: 1000, order: "mlm_sequence asc, id asc" },
    ]);
    if (children.length === 0) break;
    all.push(...children);
    queue = children.map(c => c.id);
  }
  return all;
}

// ─── Count descendants of a specific partner (supports search) ──────────────
export async function countDescendantsByRoot(uid, password, rootPartnerId, search = "") {
  const domain = [
    ["is_mlm_member", "=", true],
    ["id", "!=", rootPartnerId],
    "|",
    ["mlm_treepath", "like", `%/${rootPartnerId}/%`],
    ["parent_id", "=", rootPartnerId],
  ];
  if (search) {
    domain.push("|");
    domain.push(["name", "ilike", search]);
    domain.push(["mlm_sequence", "ilike", search]);
  }
  const count = await rpc("object", "execute_kw", [
    DB, uid, password,
    "res.partner", "search_count",
    [domain],
  ]);
  return count;
}

export async function fetchTeamMembers(uid, password, rootPartnerId, { offset = 0, limit = 10, search = "" } = {}) {
  const domain = [["is_mlm_member", "=", true]];
  if (rootPartnerId) {
    domain.push(["id", "!=", rootPartnerId]);
    domain.push("|");
    domain.push(["mlm_treepath", "like", `%/${rootPartnerId}/%`]);
    domain.push(["parent_id", "=", rootPartnerId]);
  }

  if (search) {
    domain.push("|");
    domain.push(["name", "ilike", search]);
    domain.push(["mlm_sequence", "ilike", search]);
  }

  const [records, total] = await Promise.all([
    rpc("object", "execute_kw", [
      DB, uid, password,
      "res.partner", "search_read",
      [domain],
      { fields: MLM_FIELDS, offset, limit, order: "name asc" }
    ]),
    rpc("object", "execute_kw", [
      DB, uid, password,
      "res.partner", "search_count",
      [domain]
    ])
  ]);
  return { records, total };
}

export async function fetchRankHistory(uid, password, partnerId) {
  try {
    const records = await rpc("object", "execute_kw", [
      DB, uid, password,
      "mlm.position.history", "search_read",
      [[["partner_id", "=", partnerId]]],
      { fields: ["change_date", "post_id", "total_transaction", "total_commission"], order: "change_date desc" }
    ]);
    return records || [];
  } catch (e) {
    console.error("Failed to fetch rank history:", e);
    return [];
  }
}

export async function fetchPostIncentives(uid, password) {
  try {
    const records = await rpc("object", "execute_kw", [
      DB, uid, password,
      "mlm.post.incentive", "search_read",
      [[]],
      { fields: ["serial_number", "name", 'min_legs', "commission_percentage", "min_budget"], order: "serial_number asc" }
    ]);
    return records || [];
  } catch (e) {
    console.error("Failed to fetch post incentives:", e);
    return [];
  }
}

/**
 * Changes the password for a user.
 * Uses Admin Proxy because regular users often don't have 'write' access
 * to their own res.users record in Odoo.
 */
export async function changeUserPassword(uid, oldPass, newPass) {
  const ADMIN_USER = process.env.REACT_APP_ADMIN_USER || "admin";
  const ADMIN_PASS = process.env.REACT_APP_ADMIN_PASS || "Xamta@2050";

  // 1. Fetch the user's login first (needed for verification)
  const userRecs = await rpc("object", "execute_kw", [
    DB, uid, oldPass,
    "res.users", "read",
    [[uid], ["login"]]
  ]);
  if (!userRecs || !userRecs[0]) throw new Error("Could not find user.");
  const login = userRecs[0].login;

  // 2. Verify current password by attempting to re-authenticate
  try {
    const verifiedUid = await authenticate(login, oldPass);
    if (verifiedUid !== uid) throw new Error("Authentication failed.");
  } catch (e) {
    throw new Error("Incorrect current password.");
  }

  // 3. Authenticate as Admin to perform the write
  let adminUid;
  try {
    adminUid = await authenticate(ADMIN_USER, ADMIN_PASS);
  } catch (e) {
    throw new Error("Admin proxy authentication failed.");
  }

  // 4. Perform the write using admin credentials
  return await rpc("object", "execute_kw", [
    DB, adminUid, ADMIN_PASS,
    "res.users", "write",
    [[uid], { password: newPass }]
  ]);
}

export async function forgotPassword(login) {
  const ADMIN_USER = process.env.REACT_APP_ADMIN_USER || "admin";
  const ADMIN_PASS = process.env.REACT_APP_ADMIN_PASS || "Xamta@2050";

  let adminUid;
  try {
    adminUid = await authenticate(ADMIN_USER, ADMIN_PASS);
  } catch (e) {
    throw new Error("Admin proxy authentication failed.");
  }

  const users = await rpc("object", "execute_kw", [
    DB, adminUid, ADMIN_PASS,
    "res.users", "search",
    [[["login", "=", login]]]
  ]);

  if (!users || users.length === 0) {
    throw new Error("User not found.");
  }

  return await rpc("object", "execute_kw", [
    DB, adminUid, ADMIN_PASS,
    "res.users", "action_reset_password",
    [users]
  ]);
}

/**
 * Direct password reset using Admin Proxy.
 * USE WITH CAUTION: This bypasses old password verification.
 * Only safe if the user's session is already verified.
 */
export async function resetPasswordDirect(uid, newPass) {
  const ADMIN_USER = process.env.REACT_APP_ADMIN_USER || "admin";
  const ADMIN_PASS = process.env.REACT_APP_ADMIN_PASS || "Xamta@2050";

  let adminUid;
  try {
    adminUid = await authenticate(ADMIN_USER, ADMIN_PASS);
  } catch (e) {
    throw new Error("Admin proxy authentication failed.");
  }

  // Perform the write using admin credentials
  return await rpc("object", "execute_kw", [
    DB, adminUid, ADMIN_PASS,
    "res.users", "write",
    [[uid], { password: newPass }]
  ]);
}